'use client'

import { useState, useEffect, useCallback } from "react"
import {
  Home,
  LayoutGrid,
  ScrollText,
  Phone,
  Sparkles,
  Crown,
} from "lucide-react"

/**
 * Boutons de navigation flottants — SADIYA STYLE CUIR
 *
 * Colonne verticale à gauche (bottom-6 left-6), toujours visible.
 * Contient UNIQUEMENT les 6 liens de navigation :
 *   - Accueil (#hero)
 *   - Rayons (#rayons)
 *   - Collection Privilège (/collection-privee)
 *   - Maison Eva Diaw (/boutique-eva-diaw)
 *   - Notre Histoire (#histoire)
 *   - Contact (#contact)
 *
 * Le bouton "Retour en haut" est géré séparément dans <ChatbotWidget />,
 * positionné à droite au-dessus du FAB chatbot — pattern UX standard
 * (navigation à gauche, utilitaires à droite).
 *
 * Chaque bouton est un cercle or/cuir avec icône lucide + tooltip latéral.
 */

type NavItem = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  external?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { href: "#hero", label: "Accueil", icon: Home },
  { href: "#rayons", label: "Rayons", icon: LayoutGrid },
  { href: "/collection-privee", label: "Collection Privilège", icon: Sparkles, external: true },
  { href: "/boutique-eva-diaw", label: "Maison Eva Diaw", icon: Crown, external: true },
  { href: "#histoire", label: "Notre Histoire", icon: ScrollText },
  { href: "#contact", label: "Contact", icon: Phone },
]

export function NavigationButtons() {
  const [hovered, setHovered] = useState<number | null>(null)

  const handleClick = useCallback((item: NavItem) => {
    if (item.external) {
      // navigation côté client via window.location pour les routes internes
      window.location.href = item.href
      return
    }
    if (item.href.startsWith("#")) {
      const el = document.querySelector(item.href)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }
  }, [])

  return (
    <div
      className="fixed left-4 sm:left-6 bottom-6 z-40 flex flex-col gap-2.5"
      aria-label="Navigation rapide"
    >
      {NAV_ITEMS.map((item, idx) => {
        const Icon = item.icon
        const isHovered = hovered === idx
        return (
          <button
            key={item.href}
            type="button"
            onClick={() => handleClick(item)}
            onMouseEnter={() => setHovered(idx)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(idx)}
            onBlur={() => setHovered(null)}
            aria-label={item.label}
            className="group relative h-11 w-11 rounded-full glass-gold border border-gold/50 shadow-luxury flex items-center justify-center text-cuir hover:gradient-gold hover:text-cuir transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
          >
            <Icon className="h-[18px] w-[18px]" />
            {/* Tooltip latéral */}
            <span
              className={`pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md gradient-cuir px-3 py-1.5 text-xs font-medium tracking-wide text-primary-foreground shadow-luxury transition-all duration-200 ${
                isHovered
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-1"
              }`}
            >
              {item.label}
              <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-cuir" />
            </span>
          </button>
        )
      })}
    </div>
  )
}
